/**
 * A quem se refere um evento de alerta?
 *
 * Não é pergunta trivial porque existem DOIS escopos e a tabela guarda os
 * dois na mesma forma. As regras por equipamento gravam `equipment_id`. A
 * Camada 2 (`site_power_outage`) é cross-device: grava `equipment_id = NULL`
 * e identifica o site pelo payload. Quem lê a coluna e nada mais conclui que
 * o evento não tem dono — foi assim que a queda de energia apareceu na tabela
 * de Eventos com a coluna "Ativo" em branco, sendo a linha mais grave dali.
 *
 * Três telas mostram evento (Eventos do admin, Alertas do ativo,
 * Ocorrências), e a resposta precisa ser a mesma nas três. Por isso mora
 * aqui e não em cada uma — duas cópias desta regra é como o buraco começou.
 */

import type { AlertEvent } from "@/types";

export type EscopoDoEvento = "equipamento" | "site" | "desconhecido";

export interface AlvoDoEvento {
  escopo: EscopoDoEvento;
  /** Nome legível, quando dá pra resolver. */
  nome: string | null;
}

type EventoParcial = Pick<AlertEvent, "equipment_id" | "payload">;

function textoDoPayload(payload: Record<string, unknown>, chave: string): string | null {
  const v = payload?.[chave];
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

/**
 * `nomePorEquipamento` é opcional: sem ele o escopo ainda é identificado
 * corretamente, só o nome do ativo fica pendente do id. O escopo nunca
 * depende de conseguir resolver o nome — confundir as duas coisas faria um
 * evento de equipamento desconhecido passar por evento de site.
 */
export function alvoDoEvento(
  evento: EventoParcial,
  nomePorEquipamento?: Map<string, string>,
): AlvoDoEvento {
  const payload = (evento.payload ?? {}) as Record<string, unknown>;

  if (evento.equipment_id) {
    const nome = nomePorEquipamento?.get(evento.equipment_id)
      ?? textoDoPayload(payload, "equipment_name")
      ?? null;
    return { escopo: "equipamento", nome };
  }

  // Sem equipment_id: é evento de site se o payload souber dizer qual.
  const nomeDoSite = textoDoPayload(payload, "site_name");
  const idDoSite = textoDoPayload(payload, "site_id");
  if (nomeDoSite || idDoSite) {
    return { escopo: "site", nome: nomeDoSite };
  }

  return { escopo: "desconhecido", nome: null };
}
